import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, UserPlus, ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";
import { formatDate, clientName, formatApiError } from "../lib/format";
import { DIVISIONS } from "../lib/constants";

// Modal quick-assign PIC untuk semua booking aktif tanpa PIC.
// Sumber data: prop `bookings` (dari Dashboard). Setelah save akan panggil onSaved()
// supaya dashboard refresh list.
export default function AssignPicModal({ open, setOpen, bookings, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [pending, setPending] = useState({}); // { [bookingId]: "PIC name" }

  useEffect(() => {
    if (!open) return;
    setLoadingEmp(true);
    api.get("/employees?status=active")
      .then(({ data }) => setEmployees(data || []))
      .catch(() => setEmployees([]))
      .finally(() => setLoadingEmp(false));
  }, [open]);

  // Filter no-PIC bookings (safety: recompute walau caller sudah kirim)
  const today = new Date().toISOString().slice(0,10);
  const activeStatuses = new Set(["Confirmed", "Tentative"]);
  const noPicList = useMemo(() => (bookings || []).filter((b) =>
    activeStatuses.has(b.status) && b.event_date && b.event_date >= today &&
    !(b.assigned_admin || "").trim() &&
    !((b.coordinator_user_ids || []).length)
  ), [bookings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Text search (client name, event, code, venue)
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return noPicList;
    return noPicList.filter((b) => {
      const name = clientName(b.client || b);
      return [b.booking_code, b.event_name, b.venue, b.location, name].some((v) =>
        String(v || "").toLowerCase().includes(term));
    });
  }, [noPicList, q]);

  const save = async (bid) => {
    const name = (pending[bid] || "").trim();
    if (!name) { toast.error("Pilih PIC terlebih dahulu"); return; }
    setSavingId(bid);
    try {
      await api.put(`/bookings/${bid}`, { assigned_admin: name });
      toast.success("PIC ditetapkan");
      // remove local row optimistically
      setPending((p) => { const c = { ...p }; delete c[bid]; return c; });
      if (onSaved) onSaved(bid, name);
    } catch (e) {
      if (!e?.__preview_blocked) toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menetapkan PIC");
    } finally { setSavingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="assign-pic-modal" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4"/> Tetapkan PIC — Triase Booking</DialogTitle>
          <DialogDescription>
            {noPicList.length === 0
              ? "Semua booking aktif sudah punya PIC. Kerja bagus!"
              : `Ada ${noPicList.length} booking aktif yang belum punya PIC. Pilih penanggung jawab untuk setiap booking dan simpan langsung di sini — tanpa perlu buka satu-per-satu.`}
          </DialogDescription>
        </DialogHeader>

        {noPicList.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]"/>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cari kode/klien/acara/venue…"
                className="pl-9 h-9" data-testid="pic-search"/>
            </div>
            <div className="text-[11px] text-[#8C9096] whitespace-nowrap hidden sm:block">
              {rows.length} dari {noPicList.length} tampil
            </div>
          </div>
        )}

        {noPicList.length === 0 ? (
          <div className="py-10 text-center space-y-2">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto"/>
            <div className="text-sm font-semibold text-[#1E2022]">Semua booking sudah ada PIC-nya</div>
            <div className="text-xs text-[#8C9096]">Tidak ada yang perlu di-triage sekarang.</div>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2" data-testid="pic-rows">
            {rows.length === 0 && (
              <div className="py-8 text-center text-sm text-[#8C9096]">Tidak ada hasil untuk "{q}"</div>
            )}
            <ul className="divide-y divide-[#F0EBE0]">
              {rows.map((b) => {
                const divShorts = (b.divisions || []).map((d) => d.short || (DIVISIONS.find(x => x.id === d.id)?.short)).filter(Boolean);
                return (
                  <li key={b.id} className="py-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 md:gap-3 items-start md:items-center" data-testid={`pic-row-${b.booking_code}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-brand">{b.booking_code}</span>
                        <span className="text-sm font-medium text-[#1E2022] truncate">{b.event_name}</span>
                      </div>
                      <div className="text-[11px] text-[#8C9096] mt-0.5 truncate">
                        {clientName(b.client || b)} · {formatDate(b.event_date)}
                        {b.venue && ` · ${b.venue}`}
                        {divShorts.length > 0 && ` · ${divShorts.join(", ")}`}
                      </div>
                    </div>
                    <Select value={pending[b.id] || ""} onValueChange={(v) => setPending((p) => ({ ...p, [b.id]: v }))}>
                      <SelectTrigger className="w-full md:w-56 h-9 text-xs" data-testid={`pic-select-${b.booking_code}`}>
                        <SelectValue placeholder={loadingEmp ? "Memuat…" : "Pilih PIC…"}/>
                      </SelectTrigger>
                      <SelectContent>
                        {employees.length === 0 && (
                          <div className="p-2 text-xs text-[#8C9096]">Belum ada karyawan aktif</div>
                        )}
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.name} data-testid={`pic-opt-${e.id}`}>
                            <div className="flex flex-col">
                              <span className="text-sm">{e.name}</span>
                              <span className="text-[10px] text-[#8C9096]">{e.role || "-"}{e.division_id ? ` · ${DIVISIONS.find(d => d.id === e.division_id)?.short || ""}` : ""}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => save(b.id)} disabled={!pending[b.id] || savingId === b.id}
                        className="bg-brand text-white hover:bg-brand/90 h-9 whitespace-nowrap"
                        data-testid={`pic-save-${b.booking_code}`}>
                        {savingId === b.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1"/> : null}
                        Simpan
                      </Button>
                      <Link to={`/bookings/${b.id}`} className="text-[11px] text-brand hover:underline inline-flex items-center gap-0.5" data-testid={`pic-open-${b.booking_code}`}>
                        Buka <ArrowRight className="w-3 h-3"/>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
